import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { ReportService } from '../../service/ReportService';
import ReactApexChart from 'react-apexcharts';
import { Range } from 'react-date-range';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { FilterBy, ViewType } from './TokenUsage';
import { TokenUsage } from '../../util/Types/TokenUsage';
import { generateData, generateLabels, getChartOptions } from './helpers';
import { Box } from '@mui/material';

interface Props {
  selectedDateRange: Range;
  queryString: string;
  differentYearsSelected: number[];
  filteredBy: FilterBy;
  viewType: ViewType;
}

const TextUsage: React.FC<Props> = ({
  selectedDateRange,
  queryString,
  differentYearsSelected,
  filteredBy,
  viewType,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const authUser = useAuthStore(state => state.user);

  const [textUsage, setTextUsage] = useState<TokenUsage[]>([]);
  const [completionUsage, setCompletionUsage] = useState<TokenUsage[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const getTextUsage = async () => {
      setLoaded(false);
      try {
        if (differentYearsSelected.length > 0) {
          const viewPrefix =
            viewType === ViewType.Company
              ? `companyId=${authUser?.selectedCompanyId}&`
              : viewType === ViewType.Both
                ? `companyId=${authUser?.selectedCompanyId}&both=true&`
                : '';
          let firstQueryString = `${viewPrefix}year=${differentYearsSelected[0]}&months=1,2,3,4,5,6,7,8,9,10,11,12`;
          let secondQueryString = `${viewPrefix}year=${differentYearsSelected[1]}&months=1,2,3,4,5,6,7,8,9,10,11,12`;
          const [firstResult, secondResult, firstCompletion, secondCompletion] =
            await Promise.all([
              ReportService.getTextUsage(authUser!.id, firstQueryString),
              ReportService.getTextUsage(authUser!.id, secondQueryString),
              ReportService.getCompletionUsage(authUser!.id, firstQueryString),
              ReportService.getCompletionUsage(authUser!.id, secondQueryString),
            ]);
          setTextUsage([...firstResult.data, ...secondResult.data]);
          setCompletionUsage([
            ...firstCompletion.data,
            ...secondCompletion.data,
          ]);
        } else {
          const [response, completionResponse] = await Promise.all([
            ReportService.getTextUsage(authUser!.id, queryString),
            ReportService.getCompletionUsage(authUser!.id, queryString),
          ]);

          setTextUsage(response.data);
          setCompletionUsage(completionResponse.data);
        }
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }

      setLoaded(true);
    };

    getTextUsage();
  }, [authUser, queryString, differentYearsSelected, viewType]);

  const { xAxisLabels, selectBy } = generateLabels(
    filteredBy,
    selectedDateRange,
    differentYearsSelected,
  );

  const isBoth = viewType === ViewType.Both;
  const ownCompletion = completionUsage.filter(
    el => el.label !== 'CompletionOthers',
  );
  const othersCompletion = completionUsage.filter(
    el => el.label === 'CompletionOthers',
  );

  const series = [
    {
      name: 'Text',
      data: generateData(selectBy, xAxisLabels, textUsage, 'Text').map(
        el => el.usedTokens,
      ),
    },
    {
      name: isBoth ? 'Completion (you)' : 'Completion',
      data: generateData(
        selectBy,
        xAxisLabels,
        ownCompletion,
        'Completion',
      ).map(el => el.usedTokens),
    },
  ];

  if (isBoth) {
    series.push({
      name: 'Completion (other members)',
      data: generateData(
        selectBy,
        xAxisLabels,
        othersCompletion,
        'CompletionOthers',
      ).map(el => el.usedTokens),
    });
  }

  const colors = isBoth
    ? ['#396AFC', '#9A8AC0', '#F2994A']
    : ['#396AFC', '#9A8AC0'];

  return loaded ? (
    <Box
      sx={{
        border: '1px solid rgba(234, 236, 240, 1)',
        padding: '16px',
        borderRadius: '4px',
        marginTop: '10px',
      }}
    >
      <ReactApexChart
        options={getChartOptions(
          xAxisLabels,
          colors,
          generateData(selectBy, xAxisLabels, textUsage, 'Text').map(
            el => el.price,
          ),
        )}
        series={series}
        type="bar"
        height={350}
      />
    </Box>
  ) : null;
};

export default TextUsage;
